import {
  EyeOff,
  Hurricane03,
  LayersThree02,
  Settings04,
  SpacingHeight01,
  Type01,
  TypeStrikethrough01,
} from '@timelyapp/icons/solid-icons';
import type {
  MemoryConstructionParams,
  SettingsRangeEnums,
} from '@timelyapp/integration-cloud-api-spec';
import { getSetting } from '@timelyapp/integration-cloud-sdk/utils/memory_config/utils.setting-to-value-map';
import type { DropdownMenuItem } from '@timelyapp/react';
import { Box, CustomIcon, Switch } from '@timelyapp/react';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { setOpenMemoryConfig } from 'redux/modules/tic/tic.global';
import { saveMemoryConfig } from 'redux/modules/tic/tic.memories.actions';
import { getMemoryConfig } from 'redux/modules/tic/tic.memories.config.selectors';
import noop from 'utils/noop';
import { getTicAiPreferenceShowTimelinePredictions } from '~domains/ai/predictions/redux/predictions.selectors';
import { saveAiPreferences } from '~domains/ai/preferences/redux/ai.preferences.actions';
import { getIsTicAiFeatureAvailable } from '~domains/ai/redux/ai.availability.selectors';
import { useMemoryPreferencesResetOptions } from './useMemoryPreferencesResetOptions';

const DROPDOWN_WIDTH_PX = 330 as const;
/**
 * Hook for handling memory display options and actions
 * Returns dropdown menu items with handlers already configured.
 */
type Props = {
  /** Adjust the returned items to a specific type of dropdown menu  */
  displayContext: 'in_lane_header_dropdown' | 'in_global_memory_header';
};
export const useMemoryDisplayOptions = ({ displayContext }: Props) => {
  // !! May want this as prop instead of hook, but revisit when broader usage is known
  const memoryFilter = useSelector(getMemoryConfig);
  const showTimelinePredictions = useSelector(getTicAiPreferenceShowTimelinePredictions);
  const isAiFeatureAvailable = useSelector(getIsTicAiFeatureAvailable);
  const actions = useWithDispatch({ saveMemoryConfig, setOpenMemoryConfig, saveAiPreferences });

  const { dropdownItems: resetDropdownOptions } = useMemoryPreferencesResetOptions();

  // Create memory display dropdown items
  const dropdownItems = useMemo(() => {
    const items: DropdownMenuItem[] = [];
    const additionalMemoryItems: DropdownMenuItem[] = [];

    items.push({
      type: 'label',
      label: 'Memory & Timeline Options',
    });

    // Only show the AI/suggestions Lane in global header.
    if (displayContext === 'in_global_memory_header' && isAiFeatureAvailable) {
      const aiPredictionLaneItem: DropdownMenuItem = {
        type: 'switch',
        label: 'Suggestions Lane',
        sidenote: (
          <Box.Row gap="small">
            <CustomIcon.Sparkling />
            Assistant
          </Box.Row>
        ),
        checked: showTimelinePredictions,
        onCheckedChange: (checked: boolean) => {
          const preferences = { enabled: checked };
          actions.saveAiPreferences({ preferences });
        },
      };
      items.push(aiPredictionLaneItem);
    }

    const minutesPerBlock = memoryFilter.minutesPerBlock ?? 15;
    const summaryLaneItem: DropdownMenuItem = {
      type: 'subMenu',
      label: 'Summary Lane',
      sidenote: minutesPerBlock ? `${minutesPerBlock}mins` : 'Off',
      icon: <Switch readOnly checked={!!minutesPerBlock} onChangeChecked={noop} size="xsmall" />,
      onClick: () => {
        actions.saveMemoryConfig({ minutesPerBlock: minutesPerBlock > 0 ? 0 : 15 });
      },
      subOptions: [
        {
          type: 'radioGroup',
          label: 'Minutes per summary block',
          currentValue: minutesPerBlock,
          onValueChange: (value: string | number | boolean) => {
            const minutes = typeof value === 'number' ? value : parseInt(value as string, 10);
            actions.saveMemoryConfig({ minutesPerBlock: minutes });
          },
          subOptions: [
            { type: 'radioItem', label: '5 minutes', value: 5 },
            { type: 'radioItem', label: '10 minutes', value: 10 },
            { type: 'radioItem', label: '15 minutes', value: 15 },
            { type: 'radioItem', label: '30 minutes', value: 30 },
            { type: 'radioItem', label: '1 hour', value: 60 },
            { type: 'radioItem', icon: <EyeOff />, label: 'Turn off Summaries', value: 0 },
          ],
        },
        {
          type: 'separator',
        },
        {
          type: 'message',
          label: 'About Summary Blocks',
          description: 'Displays a summary of the most active memories in a given time interval.',
          variant: 'info',
        },
      ],
    };

    // Only show the summary lane in global header.
    if (displayContext === 'in_global_memory_header') {
      items.push(summaryLaneItem);
    }
    // Group: Show Other Memories Lane (only if summary blocks are enabled)
    if (minutesPerBlock > 0) {
      items.push({
        type: 'switch',
        label: displayContext === 'in_global_memory_header' ? 'Memories Lane' : 'Show all memories',
        checked: memoryFilter.showOtherMemoriesLane,
        onCheckedChange: (checked: boolean) => {
          actions.saveMemoryConfig({ showOtherMemoriesLane: checked });
        },
      });
    }

    items.push({ type: 'separator' });

    /**
     * Start adding submenu items
     */
    additionalMemoryItems.push({
      type: 'label',
      label: 'Memory & Timeline options',
    });

    // Group: Compact Memories
    const isCompact = memoryFilter.compact;
    const compactItem: DropdownMenuItem = {
      type: 'item',
      label: isCompact ? 'Show titles' : 'Hide titles',
      icon: isCompact ? <Type01 /> : <TypeStrikethrough01 />,
      onSelect: () => {
        actions.saveMemoryConfig({ compact: !isCompact });
      },
    };

    if (displayContext === 'in_global_memory_header') {
      additionalMemoryItems.push(compactItem);
    } else {
      items.push(compactItem);
    }

    // Add group by as a switch
    const groupByValue = memoryFilter.groupMemoriesBy;
    const isGroupByTool = groupByValue === 'tool';

    // Add group by as a submenu
    additionalMemoryItems.push({
      type: 'subMenu',
      label: 'Grouping',
      icon: <LayersThree02 />,
      width: DROPDOWN_WIDTH_PX,
      sidenote: isGroupByTool ? 'On' : `Don't group`,
      subOptions: [
        {
          type: 'radioGroup',
          label: `Grouping similar memories`,
          currentValue: groupByValue,
          onValueChange: (groupMemoriesBy: any) => {
            actions.saveMemoryConfig({ groupMemoriesBy });
          },
          subOptions: [
            {
              type: 'radioItem',
              label: 'Group nearby memories',
              value: 'tool',
              description: `Memories from same app or website may merge and display as one longer memory`,
            },
            {
              type: 'radioItem',
              label: `Don't group`,
              value: 'title',
            },
          ],
        },
        { type: 'separator' },
        {
          type: 'message',
          description: `Should nearby memories from the same app or website should group together?`,
          variant: 'info',
          icon: 'auto',
        },
      ],
    });

    // Group: Continuity
    const continuitySetting = getSetting('continuity', memoryFilter.mergeWithinMinutes);
    additionalMemoryItems.push({
      type: 'subMenu',
      label: 'Maintain continuity',
      sidenote: continuitySetting.label,
      icon: <SpacingHeight01 />,
      subOptions: [
        {
          type: 'radioGroup',
          label: 'Merge close memories',
          currentValue: continuitySetting.key,
          onValueChange: (value: string | number | boolean) => {
            const level = value as SettingsRangeEnums;
            const setting = getSetting('continuity', level);
            actions.saveMemoryConfig({ mergeWithinMinutes: setting.value as number });
          },
          subOptions: continuitySetting.options.map(option => ({
            type: 'radioItem',
            label: option.label,
            value: option.key,
            sidenote: option.sidenote,
          })),
        },
        {
          type: 'separator',
        },
        {
          type: 'message',
          description: continuitySetting.metadata.description,
          variant: 'info',
          icon: 'auto',
        },
      ],
    });

    const noiseCancellationSetting = getSetting(
      'noise_cancellation',
      memoryFilter.minimumGroupDurationMinutes
    );
    // Group: Noise Cancellation
    additionalMemoryItems.push({
      type: 'subMenu',
      label: 'Noise Cancellation',
      sidenote: noiseCancellationSetting.label,
      icon: <Hurricane03 />,
      subOptions: [
        {
          type: 'radioGroup',
          label: noiseCancellationSetting.metadata.title,
          currentValue: noiseCancellationSetting.key,
          onValueChange: (value: string | number | boolean) => {
            const level = value as SettingsRangeEnums;
            const setting = getSetting('noise_cancellation', level);
            actions.saveMemoryConfig({ minimumGroupDurationMinutes: setting.value as number });
          },
          subOptions: noiseCancellationSetting.options.map(option => ({
            type: 'radioItem',
            label: option.label,
            value: option.key,
            sidenote: option.sidenote,
          })),
        },
        {
          type: 'separator',
        },
        {
          type: 'message',
          description: noiseCancellationSetting.metadata.description,
          variant: 'info',
        },
      ],
    });

    // Group: Naming Strategy
    const namingStrategySetting = getSetting(
      'naming_strategy',
      memoryFilter.namingStrategy as 'title'
    );
    additionalMemoryItems.push({
      type: 'subMenu',
      label: namingStrategySetting.metadata.title,
      sidenote: namingStrategySetting.sidenote,
      icon: <Type01 />,
      subOptions: [
        {
          type: 'radioGroup',
          label: 'Inherit title from',
          currentValue: namingStrategySetting.key,
          onValueChange: (value: string | number | boolean) => {
            actions.saveMemoryConfig({
              namingStrategy: value as MemoryConstructionParams['namingStrategy'],
            });
          },
          subOptions: namingStrategySetting.options.map(option => ({
            type: 'radioItem',
            label: option.label,
            value: option.key,
          })),
        },
        {
          type: 'separator',
        },
        {
          type: 'message',
          description: namingStrategySetting.metadata.description,
          variant: 'info',
          icon: 'auto',
        },
      ],
    });

    additionalMemoryItems.push({
      type: 'separator',
    });

    // Group: Show Hidden Memories
    additionalMemoryItems.push({
      type: 'switch',
      label: 'Show hidden Memories',
      checked: memoryFilter.showIgnored,
      onCheckedChange: checked => {
        actions.saveMemoryConfig({ showIgnored: checked });
      },
    });
    additionalMemoryItems.push({
      type: 'item',
      label: 'View all display options',
      closeOnSelect: true,
      icon: <Settings04 />,
      onSelect: () => {
        actions.setOpenMemoryConfig('global');
      },
    });

    additionalMemoryItems.push(...resetDropdownOptions);

    items.push({
      type: 'subMenu',
      label: 'Adjust',
      icon: <Settings04 />,
      width: DROPDOWN_WIDTH_PX,
      subOptions: additionalMemoryItems,
    });

    return items;
  }, [
    displayContext,
    isAiFeatureAvailable,
    memoryFilter.minutesPerBlock,
    memoryFilter.compact,
    memoryFilter.groupMemoriesBy,
    memoryFilter.mergeWithinMinutes,
    memoryFilter.minimumGroupDurationMinutes,
    memoryFilter.namingStrategy,
    memoryFilter.showIgnored,
    memoryFilter.showOtherMemoriesLane,
    resetDropdownOptions,
    showTimelinePredictions,
    actions,
  ]);

  return {
    dropdownItems,
    dropdownWidthPx: DROPDOWN_WIDTH_PX,
  };
};

export default useMemoryDisplayOptions;
